import React from 'react';

import {
  AdditionalFields,
  FilterList,
} from './types/SectionFilterableArticles.types';

import {
  BackButton,
  BackButtonContainer,
  DynamicHeaderWrapper,
  HeaderWrapper,
  HeadingTextWrapper,
  SecondaryBackLabel,
} from './styles/FilterableArticlesHeader.styles';

import RichText from '../../Elements/RichText';
import Heading from '@legacy-vfuk/core-heading';
import RedText from '../../../../templates/RedText';
import Icon from '@legacy-vfuk/core-icon';
import { stripRedTextMarkers } from '../../../../helpers/formatters';
import { navigate } from 'gatsby';

export type FilterableArticlesHeaderProps = {
  filterList: FilterList;
  additionalFields: AdditionalFields;
  filtersApplied: boolean;
  searchQuery: string;
  isMediaView: boolean;
  direction: string;
};

const FilterableArticlesHeader = ({
  filterList,
  additionalFields,
  filtersApplied,
  isMediaView,
  searchQuery,
  direction,
}: FilterableArticlesHeaderProps) => {
  const checkAndHandleBackButtonClick = () => {
    navigate(`${location.origin}${location.pathname}`, { replace: true });
  };

  const generateHeaderText = (isMedia: boolean) => {
    let currentHeader = '';
    if (
      filterList.topic.length < 1 &&
      filterList.contentType.length < 1 &&
      filterList.latest &&
      !filterList.recentlyViewed &&
      searchQuery.length < 1
    ) {
      currentHeader = isMedia
        ? additionalFields.uiTextMediaLatestHeader
        : additionalFields.uiTextLatestHeader;
    } else if (
      filterList.topic.length < 1 &&
      filterList.contentType.length < 1 &&
      filterList.mostPopular &&
      !filterList.recentlyViewed &&
      searchQuery.length < 1
    ) {
      currentHeader = isMedia
        ? additionalFields.uiTextMediaPopularityHeader
        : additionalFields.uiTextPopularityHeader;
    } else if (
      filterList.topic.length < 1 &&
      filterList.contentType.length < 1 &&
      filterList.default &&
      !filterList.recentlyViewed &&
      searchQuery.length < 1
    ) {
      currentHeader = isMedia
        ? additionalFields.uiTextAllMediaHeader
        : additionalFields.uiTextAllHeader;
    } else if (filterList.recentlyViewed) {
      currentHeader = isMedia
        ? additionalFields.uiTextRecentlyViewedMediaHeader
        : additionalFields.uiTextRecentlyViewedHeader;
    } else if (
      filterList.topic.length < 1 &&
      searchQuery.length < 1 &&
      !filterList.recentlyViewed &&
      filterList.contentType.length > 0
    ) {
      currentHeader = isMedia
        ? additionalFields.uiTextFilteredContentTypeMediaHeader
        : additionalFields.uiTextFilteredContentTypeHeader;
      const categoriesString = filterList.contentType
        .map(cType => cType.displayName)
        .join(', ');
      currentHeader = currentHeader.replace('{contentTypes}', categoriesString);
    } else if (
      filterList.topic.length > 0 &&
      searchQuery.length < 1 &&
      !filterList.recentlyViewed
    ) {
      currentHeader = isMedia
        ? additionalFields.uiTextFilteredTopicMediaHeader
        : additionalFields.uiTextFilteredTopicHeader;
      const topicsString = filterList.topic.replace(/,/g, ', ').trim();
      currentHeader = currentHeader.replace('{topics}', topicsString);
    } else if (searchQuery.length > 0 && !filterList.recentlyViewed) {
      currentHeader = additionalFields.uiTextSearchedTermHeader.replace(
        '{searchedTerm}',
        `"${searchQuery}"`
      );
    } else if (isMedia) {
      currentHeader = additionalFields.uiTextMediaHeader;
    }

    return currentHeader;
  };

  const getSecondaryBackText = () => {
    if (filterList.selectedTopics && filterList.selectedTopics.length > 0) {
      return stripRedTextMarkers(filterList.selectedTopics[0].displayName);
    }

    if (filterList.media) {
      return stripRedTextMarkers(additionalFields.uiTextAllMediaHeader);
    }

    return stripRedTextMarkers(additionalFields.uiTextAllHeader);
  };

  const generateDynamicHeader = () => {
    const currentHeader = generateHeaderText(filterList.media);

    if (
      currentHeader.includes('RedTextStart:') &&
      currentHeader.includes(':RedTextEnd')
    ) {
      const redTextMatch = currentHeader.match(
        /RedTextStart:(.*?)\:RedTextEnd/
      );
      const redText = redTextMatch ? redTextMatch[1] : '';

      const [before, after] = currentHeader.split(
        /RedTextStart:.*?:RedTextEnd/
      );

      return (
        <Heading level={2}>
          <HeadingTextWrapper>
            {before && <>{before.trim()} </>}
          </HeadingTextWrapper>
          <RedText redText={redText} />
          <HeadingTextWrapper>
            {after && <> {after.trim()}</>}
          </HeadingTextWrapper>
        </Heading>
      );
    } else {
      return (
        <DynamicHeaderWrapper>
          <Heading level={2}>{currentHeader}</Heading>
        </DynamicHeaderWrapper>
      );
    }
  };

  return (
    <>
      {filtersApplied || isMediaView ? (
        <BackButtonContainer>
          <BackButton onClick={checkAndHandleBackButtonClick}>
            <p>{additionalFields.uiTextBackButtonLabel}</p>
            <Icon
              name={direction === 'rtl' ? 'chevron-left' : 'chevron-right'}
              size={2}
            />
          </BackButton>
          <SecondaryBackLabel>
            <p>{getSecondaryBackText()}</p>
          </SecondaryBackLabel>
        </BackButtonContainer>
      ) : null}
      <HeaderWrapper>
        {filtersApplied || isMediaView ? (
          generateDynamicHeader()
        ) : (
          <RichText text={additionalFields.uiTextDefaultHeader} />
        )}
      </HeaderWrapper>
    </>
  );
};

export default FilterableArticlesHeader;
